import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Shield, Copy, RefreshCw, Clock, Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportPin() {
  const [pin, setPin] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [accessLogs, setAccessLogs] = useState([]);
  const [showAccessLogs, setShowAccessLogs] = useState(false);

  const fetchCurrentPin = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support-pin/current`);
      if (response.data.has_active_pin) {
        setPin(response.data.pin);
        setExpiresAt(response.data.expires_at);
        setRemainingSeconds(response.data.remaining_seconds);
      } else {
        setPin(null);
        setExpiresAt(null);
        setRemainingSeconds(0);
      }
    } catch (error) {
      console.error('Failed to fetch current PIN:', error);
    }
  }, []);

  const fetchAccessLogs = async () => {
    try {
      const response = await axios.get(`${API}/support-pin/my-access-logs`);
      setAccessLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch access logs:', error);
    }
  };

  useEffect(() => {
    fetchCurrentPin();
    fetchAccessLogs();
  }, [fetchCurrentPin]);

  // Countdown timer
  useEffect(() => {
    if (remainingSeconds > 0) {
      const timer = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            setPin(null);
            setExpiresAt(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [remainingSeconds]);

  const generatePin = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/support-pin/generate`);
      setPin(response.data.pin);
      setExpiresAt(response.data.expires_at);
      setRemainingSeconds(response.data.expires_in_minutes * 60);
      setShowPin(true);
      toast.success('Support PIN generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PIN');
    } finally {
      setLoading(false);
    }
  };

  const invalidatePin = async () => {
    try {
      await axios.delete(`${API}/support-pin/invalidate`);
      setPin(null);
      setExpiresAt(null);
      setRemainingSeconds(0);
      toast.success('Support PIN invalidated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invalidate PIN');
    }
  };

  const copyPin = () => {
    if (pin) {
      navigator.clipboard.writeText(pin);
      toast.success('PIN copied to clipboard');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-blue-700">
          <Shield className="w-5 h-5" />
          Support PIN
        </CardTitle>
        <CardDescription>
          Generate a temporary PIN to allow authorized support personnel to access your dashboard
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {pin ? (
          <div className="space-y-4">
            {/* PIN Display */}
            <div className="bg-white rounded-lg border-2 border-blue-200 p-4 text-center">
              <div className="text-sm text-slate-500 mb-2">Your Support PIN</div>
              <div className="flex items-center justify-center gap-2">
                <div className="text-4xl font-mono font-bold tracking-[0.5em] text-blue-600">
                  {showPin ? pin : '******'}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPin(!showPin)}
                  className="h-8 w-8"
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Timer */}
              <div className="flex items-center justify-center gap-2 mt-3 text-amber-600">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Expires in {formatTime(remainingSeconds)}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={copyPin}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy PIN
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={generatePin}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                New PIN
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={invalidatePin}
              >
                <Lock className="w-4 h-4" />
              </Button>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Only share this PIN with authorized CloudSetu support personnel. 
                The PIN will be invalidated after one use.
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-slate-500 mb-4">
              No active Support PIN. Generate one to allow support access.
            </p>
            <Button onClick={generatePin} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Generate Support PIN
                </>
              )}
            </Button>
          </div>
        )}

        {/* Access History Link */}
        {accessLogs.length > 0 && (
          <Button
            variant="link"
            className="w-full text-slate-500 bg-[#2563eb]/10"
            onClick={() => setShowAccessLogs(true)}
          >
            View Support Access History ({accessLogs.length})
          </Button>
        )}

        {/* Access Logs Dialog */}
        <Dialog open={showAccessLogs} onOpenChange={setShowAccessLogs}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Support Access History</DialogTitle>
              <DialogDescription>
                Record of support personnel who have accessed your dashboard
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {accessLogs.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No access history</p>
              ) : (
                <div className="space-y-3">
                  {accessLogs.map((log) => (
                    <div key={log.id} className="p-3 bg-slate-50 rounded-lg border">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-700">
                            {log.accessed_by_email}
                          </p>
                          <p className="text-sm text-slate-500">
                            Role: {log.accessed_by_role}
                          </p>
                          {log.reason && (
                            <p className="text-sm text-slate-600 mt-1">
                              Reason: {log.reason}
                            </p>
                          )}
                        </div>
                        <div className="text-right text-sm text-slate-500">
                          <p>{formatDate(log.created_at)}</p>
                          {log.session_ended_at && (
                            <p className="text-green-600">Session ended</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
